import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import SpecialtyEventUploadFinishMessage from '../SpecialtyEventUploadFinishMessage';
import {
  canUploadToSpecialtyEventAlbums,
  getSpecialtyAlbums,
  shouldShowSpecialtyEventUploadButtons,
} from '../../helpers';
import SpecialtyAlbumGreetingModal, {
  SPECIALTY_ALBUM_GREETING_MODAL,
} from '../../../../../apps/pro/views/AlbumFolder/FreemiumEvents/SpecialtyAlbumGreetingModal';
import { getCelebratedPersonName } from '../../../../../apps/pro/views/AlbumFolder/FreemiumEvents/SpecialtyAlbumGreetingModal/helpers';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../constants/event-items';
import SpecialtyEventUploadAlbumBox from '../SpecialtyEventUploadAlbumBox';
import useOpenModal from '../../../../../hooks/useOpenModal';
import { isSpecialtyAlbum, isVGBContext } from '../../../../../util/album';
import { STEPS } from '../../constants';
import SpecialtyEventUploadFooter from '../SpecialtyEventUploadFooter';
import { getAsset } from '../../../../../util/assets';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  authenticated: PropTypes.bool.isRequired,
  uploadedTargets: PropTypes.array.isRequired,
  canContinueUploading: PropTypes.bool.isRequired,
  onUpload: PropTypes.func.isRequired,
  onVideoUpload: PropTypes.func.isRequired,
  success: PropTypes.bool.isRequired,
  setStep: PropTypes.func.isRequired,
};

const HomeStep = ({
  albumFolder,
  authenticated,
  uploadedTargets,
  canContinueUploading,
  onUpload,
  onVideoUpload,
  success,
  setStep,
}) => {
  const history = useHistory();
  const openModal = useOpenModal();
  const [waldofiedAlbum, memoryBookAlbum, groupVideoAlbum] = getSpecialtyAlbums(
    albumFolder?.children?.albums?.records,
    albumFolder?.album_subtype,
    [
      EVENT_ADDON_ITEM_TYPE.WALDOFIED,
      EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
      EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
    ],
  );

  const photosAlbums = (albumFolder?.children?.albums?.records ?? []).filter(
    album => !isSpecialtyAlbum(album),
  );

  const celebratedName = getCelebratedPersonName(
    albumFolder?.album_subtype,
    albumFolder?.event_settings,
    false,
    albumFolder?.account?.organization_name ?? albumFolder?.account?.name,
  );
  const possessiveCelebratedName = getCelebratedPersonName(
    albumFolder?.album_subtype,
    albumFolder?.event_settings,
    true,
    albumFolder?.account?.organization_name ?? albumFolder?.account?.name,
  );

  const isVGB = isVGBContext(albumFolder);

  const title = (() => {
    const weddingCopyIndex = (albumFolder?.name ?? '').indexOf('Wedding ❤');
    if (weddingCopyIndex !== -1) {
      return (
        <h2 className="text-black mb-2 text-center">
          {albumFolder.name.slice(0, weddingCopyIndex)}{' '}
          <br className="d-md-none" />
          {albumFolder.name.slice(weddingCopyIndex)}!
        </h2>
      );
    }

    return (
      <h2 className="text-black mb-2 text-center">{albumFolder?.name}!</h2>
    );
  })();

  return (
    <Fragment>
      <div className="d-flex flex-column align-items-center justify-content-center">
        {success ? (
          <Fragment>
            {canContinueUploading && (
              <h5 className="mb-1 text-center text-uppercase text-primary">
                Upload Success!
              </h5>
            )}
            <h2 className="text-black mb-2 text-center">
              {canContinueUploading
                ? 'What would you like to do next?'
                : 'Success!'}
            </h2>
          </Fragment>
        ) : (
          <Fragment>
            <h5 className="mb-1 text-center text-uppercase text-light">
              Welcome to
            </h5>
            {title}
          </Fragment>
        )}
      </div>
      {uploadedTargets.length > 0 && !canContinueUploading && (
        <Fragment>
          <SpecialtyEventUploadFinishMessage
            uploadedTargets={uploadedTargets}
            possessiveCelebratedName={possessiveCelebratedName}
          />
          <hr className="w-100 mt-0 my-2" />
          <h5 className="my-1 text-center text-uppercase text-primary">
            What&apos;s next?
          </h5>
        </Fragment>
      )}
      <div className="specialty-event-upload-ctas">
        {shouldShowSpecialtyEventUploadButtons(photosAlbums) && (
          <SpecialtyEventUploadAlbumBox
            isPhotosAlbums
            album={waldofiedAlbum ?? photosAlbums[0]}
            albumFolder={albumFolder}
            className="specialty-event-upload-album-button-boxes"
            onUploadButtonClick={ref => {
              if (photosAlbums.length === 1) {
                ref?.current?.click();
              } else {
                setStep(STEPS.ALBUM_SELECT);
              }
            }}
            onViewButtonClick={() => {
              history.push(`/discover/album-folder/${albumFolder.id}/start`);
            }}
            onUpload={onUpload}
            authenticated={authenticated}
            uploadLabel={
              <div className="text-center">
                <img
                  src={getAsset('/3-doors-add-icon.svg')}
                  className="d-block mb-2 mx-auto"
                  width="48"
                  alt="Add Photos Icon"
                />
                Add your photos &amp; videos now
              </div>
            }
            viewLabel={
              <div className="text-center">
                <img
                  src={getAsset('/3-doors-join-icon.svg')}
                  className="d-block mb-2 mx-auto"
                  width="48"
                  alt="Join Icon"
                />
                Join the event &amp;{' '}
                {canUploadToSpecialtyEventAlbums(photosAlbums)
                  ? 'add them later'
                  : 'get your photos!'}
              </div>
            }
          />
        )}
        {shouldShowSpecialtyEventUploadButtons([groupVideoAlbum]) && (
          <SpecialtyEventUploadAlbumBox
            album={groupVideoAlbum}
            albumFolder={albumFolder}
            onUploadButtonClick={() => onVideoUpload(groupVideoAlbum)}
            onViewButtonClick={() => {
              history.push(`/albums/${groupVideoAlbum.id}/slideshow`);
            }}
            onUpload={onUpload}
            authenticated={authenticated}
            title={`Have you left a message in ${possessiveCelebratedName} ${
              isVGB ? 'Video Guestbook' : 'Group Video'
            }?`}
            uploadLabel="Add Video Message"
            viewLabel={`View ${isVGB ? 'Guestbook' : 'Group Video'}`}
          />
        )}
        {shouldShowSpecialtyEventUploadButtons([memoryBookAlbum]) && (
          <SpecialtyEventUploadAlbumBox
            album={memoryBookAlbum}
            albumFolder={albumFolder}
            onUploadButtonClick={ref => {
              openModal(SPECIALTY_ALBUM_GREETING_MODAL, {
                albumId: memoryBookAlbum.id,
                headerless: true,
                onAddPhotos: () => {
                  ref?.current?.click?.();
                },
              });
            }}
            onViewButtonClick={() => {
              history.push(`/albums/${memoryBookAlbum.id}/slideshow`);
            }}
            onUpload={onUpload}
            authenticated={authenticated}
            title={
              canUploadToSpecialtyEventAlbums(
                [memoryBookAlbum],
                authenticated,
                albumFolder?.event_settings,
              )
                ? `Have any old photos of ${celebratedName}?`
                : `Check out ${possessiveCelebratedName} journey here!`
            }
            uploadLabel="Add old photos"
            viewLabel={
              canUploadToSpecialtyEventAlbums(
                [memoryBookAlbum],
                authenticated,
                albumFolder?.event_settings,
              )
                ? 'View slideshow'
                : 'View Memories Slideshow'
            }
          />
        )}
      </div>

      <SpecialtyEventUploadFooter
        albumFolder={albumFolder}
        authenticated={authenticated}
        setStep={setStep}
      />
      <SpecialtyAlbumGreetingModal />
    </Fragment>
  );
};

HomeStep.propTypes = propTypes;

export default HomeStep;
